/**
 * New Relic Browser Agent initialization
 * 
 * This module initializes the New Relic browser agent for frontend monitoring.
 * It requires the following environment variables:
 * - VITE_NEW_RELIC_ACCOUNT_ID
 * - VITE_NEW_RELIC_TRUST_KEY
 * - VITE_NEW_RELIC_AGENT_ID
 * - VITE_NEW_RELIC_LICENSE_KEY
 * - VITE_NEW_RELIC_APP_ID
 * 
 * Get these values from New Relic dashboard:
 * 1. Go to New Relic One > Browser > Add browser app
 * 2. Choose "Copy/Paste JavaScript code"
 * 3. Extract the values from the snippet
 */

let newRelicInitialized = false;

/**
 * Initialize New Relic Browser Agent
 * 
 * Can be called with config from PublicConfigContext or fallback to import.meta.env
 */
export function initNewRelic(config?: {
  accountId?: string;
  trustKey?: string;
  agentId?: string;
  licenseKey?: string;
  appId?: string;
}): void {
  // Use provided config or fallback to environment variables
  const accountId = config?.accountId || import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID;
  const trustKey = config?.trustKey || import.meta.env.VITE_NEW_RELIC_TRUST_KEY;
  const agentId = config?.agentId || import.meta.env.VITE_NEW_RELIC_AGENT_ID;
  const licenseKey = config?.licenseKey || import.meta.env.VITE_NEW_RELIC_LICENSE_KEY;
  const applicationId = config?.appId || import.meta.env.VITE_NEW_RELIC_APP_ID;

  // License Key is required, but Application ID is also required for Browser Monitoring
  if (!licenseKey || !applicationId) {
    console.log('New Relic Browser not configured: missing license key or application ID');
    console.log('Note: License Key can be the same as backend, but you need to create a Browser app in New Relic to get the Application ID');
    return;
  }

  if (newRelicInitialized) {
    return;
  }

  try {
    // Dynamically import the New Relic browser agent
    // This is done dynamically to avoid bundling if not configured
    import('@newrelic/browser-agent/loaders/browser-agent').then(({ BrowserAgent }) => {
      new BrowserAgent({
        init: {
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: [] },
        },
        info: {
          beacon: 'bam.nr-data.net',
          errorBeacon: 'bam.nr-data.net',
          licenseKey,
          applicationID: applicationId,
          sa: 1,
        },
        loader_config: {
          accountID: accountId,
          trustKey: trustKey || accountId,
          agentID: agentId || applicationId,
          licenseKey,
          applicationID: applicationId,
        },
      });

      newRelicInitialized = true;
      console.log('New Relic browser agent initialized');
    }).catch((error) => {
      console.warn('Failed to initialize New Relic browser agent:', error);
    });
  } catch (error) {
    console.warn('Error initializing New Relic:', error);
  }
}

/**
 * Report a custom error to New Relic
 */
export function reportError(error: Error, customAttributes?: Record<string, unknown>): void {
  if (typeof window !== 'undefined' && (window as any).newrelic) {
    (window as any).newrelic.noticeError(error, customAttributes);
  }
}

/**
 * Add custom attributes to the current page view
 */
export function setCustomAttribute(name: string, value: string | number | boolean): void {
  if (typeof window !== 'undefined' && (window as any).newrelic) {
    (window as any).newrelic.setCustomAttribute(name, value);
  }
}

/**
 * Record a custom event
 */
export function recordCustomEvent(eventType: string, attributes?: Record<string, unknown>): void {
  if (typeof window !== 'undefined' && (window as any).newrelic) {
    (window as any).newrelic.addPageAction(eventType, attributes);
  }
}

